import { useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sections } from "@/components/signal-guide/constants";
import { buildLabelMap, collectIds, buildAncestorMap } from "@/components/signal-guide/utils";
import { GuideNav } from "@/components/signal-guide/GuideNav";
import { SectionOverview } from "@/components/signal-guide/SectionOverview";
import { SectionDashboardProducts } from "@/components/signal-guide/SectionDashboardProducts";
import { SectionProductTabs } from "@/components/signal-guide/SectionProductTabs";
import { SectionBrandSignals } from "@/components/signal-guide/SectionBrandSignals";
import { SectionRankTrackerAnalytics } from "@/components/signal-guide/SectionRankTrackerAnalytics";
import { SectionDataSources } from "@/components/signal-guide/SectionDataSources";
import { SectionWorkflowsGlossary } from "@/components/signal-guide/SectionWorkflowsGlossary";
import { P } from "@/components/signal-guide/GuideHelpers";

const labelMap = buildLabelMap(sections);

export default function SignalGuide() {
  const [active, setActive] = useState("overview");
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollTo = (id: string) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleExport = () => {
    const el = contentRef.current;
    if (!el) return;

    const clone = el.cloneNode(true) as HTMLElement;

    clone.querySelectorAll("svg").forEach((svg) => svg.remove());

    clone.querySelectorAll("ol, ul").forEach((node) => {
      const listEl = node as HTMLElement;
      if (/\blist-(disc|decimal)\b/.test(listEl.className)) return;

      const replacement = document.createElement("div");
      replacement.innerHTML = listEl.innerHTML;
      replacement.className = listEl.className;
      replacement.querySelectorAll("li").forEach((li) => {
        const liReplacement = document.createElement("div");
        liReplacement.innerHTML = li.innerHTML;
        liReplacement.className = li.className;
        li.replaceWith(liReplacement);
      });
      listEl.replaceWith(replacement);
    });

    clone.querySelectorAll("*").forEach((node) => {
      const htmlEl = node as HTMLElement;
      htmlEl.removeAttribute("class");
      htmlEl.removeAttribute("id");
    });

    clone.querySelectorAll("span").forEach((span) => {
      const text = span.textContent?.trim() || "";
      if (/^\d+$/.test(text) && text.length <= 2 && span.parentElement) {
        span.textContent = text + ". ";
        span.setAttribute("style", "font-weight:700;margin-right:4px;");
      }
    });

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>Signal Guide — Signal by Recom</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>
  @page { size: 8.5in 11in; margin: 1in; }
  body {
    font-family: Calibri, Arial, Helvetica, sans-serif;
    color: #1a1a1a;
    font-size: 11pt;
    line-height: 1.5;
  }
  h1 {
    font-size: 22pt;
    font-weight: 700;
    color: #111;
    margin-top: 0;
    margin-bottom: 12pt;
    border-bottom: 2px solid #333;
    padding-bottom: 6pt;
  }
  h2 {
    font-size: 16pt;
    font-weight: 700;
    color: #1a1a1a;
    margin-top: 24pt;
    margin-bottom: 8pt;
    padding-bottom: 4pt;
    border-bottom: 1px solid #ccc;
  }
  h3 {
    font-size: 13pt;
    font-weight: 600;
    color: #222;
    margin-top: 16pt;
    margin-bottom: 6pt;
  }
  p {
    font-size: 11pt;
    color: #444;
    margin-bottom: 8pt;
    margin-top: 0;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 10pt 0 14pt 0;
  }
  th, td {
    border: 1px solid #bbb;
    padding: 6pt 10pt;
    font-size: 10pt;
    text-align: left;
    vertical-align: top;
  }
  th {
    background-color: #f0f0f0;
    font-weight: 600;
  }
  code {
    background: #f3f4f6;
    padding: 1pt 4pt;
    font-size: 9pt;
    font-family: Consolas, 'Courier New', monospace;
  }
  pre {
    background: #f3f4f6;
    padding: 10pt;
    font-size: 9pt;
    font-family: Consolas, 'Courier New', monospace;
    white-space: pre-wrap;
    margin: 8pt 0;
  }
  ul, ol {
    margin: 6pt 0 10pt 18pt;
    padding: 0;
  }
  li {
    font-size: 11pt;
    color: #444;
    margin-bottom: 4pt;
    line-height: 1.5;
  }
  div {
    margin: 0;
    padding: 0;
    border: none;
    background: none;
  }
</style>
</head>
<body>
<h1>Signal Guide — Signal by Recom</h1>
${clone.innerHTML}
</body>
</html>`;

    const blob = new Blob(['\ufeff' + html], { type: "application/msword;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Signal-Guide.doc";
    a.click();
    URL.revokeObjectURL(url);
  };

  const ancestorMap = useMemo(() => buildAncestorMap(sections), []);
  const allIds = useMemo(() => collectIds(sections), []);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => ({
    "page-overview": true,
    "data-sources": true,
    "brand-signals": false,
    analytics: false,
  }));

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    const ancestors = ancestorMap[active];
    if (!ancestors?.length) return;
    setExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const a of ancestors) {
        if (!next[a]) {
          next[a] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [active, ancestorMap]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const onScroll = () => {
      setScrolled(scrollEl.scrollTop > 160);
      const probe = scrollEl.getBoundingClientRect().top + 120;
      let current = allIds[0];
      let bestTop = -Infinity;
      for (const id of allIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= probe && top > bestTop) {
          bestTop = top;
          current = id;
        }
      }
      setActive(current);
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [allIds]);

  return (
    <div className="-ml-[29px] -mr-6 -mt-4 -mb-6 flex h-screen min-h-0 overflow-hidden bg-card">
      <aside className="sticky top-0 z-10 h-full w-64 flex-shrink-0 overflow-y-auto border-r border-border bg-card">
        <div className="pl-8 pr-4 py-6 space-y-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground">Signal Guide</h3>
            <p className="text-xs text-muted-foreground mt-0.5">How the platform works</p>
          </div>
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export for Google Docs
          </Button>
          <nav>
            <GuideNav
              nodes={sections}
              active={active}
              expanded={expanded}
              toggle={toggle}
              scrollTo={scrollTo}
            />
          </nav>
        </div>
      </aside>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-card">
        {scrolled && (
          <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border px-9 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {labelMap[active] ?? "Signal Guide"}
            </h2>
          </div>
        )}
        <div ref={contentRef} className="max-w-3xl px-9 pt-6 pb-9">
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-foreground mb-2">Signal Guide</h1>
            <P>
              This guide is the complete reference for Signal by Recom. It covers how each module works, how data flows through the platform,
              and the logic behind key features like landscape generation and bid population, SEO recommendations, and rank-to-sales correlation.
              Use it to onboard new team members, align with developers during integration, or simply understand what's happening under the hood.
            </P>
          </div>

          <SectionOverview />
          <SectionDashboardProducts />
          <SectionProductTabs />
          <SectionBrandSignals />
          <SectionRankTrackerAnalytics />
          <SectionDataSources />
          <SectionWorkflowsGlossary />

          <div className="h-20" />
        </div>
      </div>
    </div>
  );
}
